/**
 * Settings › AI vocabulary (client-MCP plan 2026-10-20 §12.5, Q15, §10.1; contract
 * `../apiv2/dashboard_ai_edit.openapi.yaml` `/ai_vocabulary/`; backend P2-09): the company's own words for our concepts
 * ("LATAM" is these regions, "Tour Leader" this staff role) that an AI client connected over MCP reads -- every entry
 * (the company's and support's first, then those derived from its set-up), the edits waiting for an approver, and
 * proposing an edit.
 *
 * - **Permissions**: explicit `vocabulary.mcp_write` (propose) or `vocabulary.mcp_approve` (read; approve in AI changes
 *   › Approvals), held per operator; Full access implies neither (`hasExplicitPerm`, never `hasPerm`), and only a holder
 *   may grant one (owner Q9, `explicit_grant_required`). The backend says what the viewer may do on the operator shown
 *   (`permissions.propose` needs the write grant on all tags; `enabled`: the feature, the operator's vocabulary domain
 *   and change sets are on).
 * - **Read-only entries**: those derived from the set-up (`editable` false, origin `auto`) are rewritten whenever the
 *   set-up changes; "Define your own" starts a company entry for the word, which the AI reads first.
 * - **Routing** (§10.1): an edit is a proposal -- applied at once (200) on a sandbox, with auto-apply, or by the
 *   viewer's own approve grant from a two-step sign-in; otherwise it waits (202) in the approvals inbox. The page says
 *   which, shows what the edit changes (the backend's records, findings such as a guide-role mismatch -- reported,
 *   never acted on -- and warnings) and reloads.
 * - **Franchise**: the page shows one operator, the sidebar's licensee (`franchise_to` from the data provider) or the
 *   viewer's own -- or, opened from a request in AI changes › Approvals (`?company=<id>`), the request's company, whatever
 *   the sidebar shows, with a link back to the sidebar's; an edit goes to the operator shown
 *   (`api.proposeVocabularyEdit`). One out of the viewer's reach is a 404, like a missing one.
 * - **The company's words** (terms, other words, definitions, a requester's reason) render as plain text inside a
 *   labelled block (`components/UntrustedText`), never as markup. Nothing guests see changes: the vocabulary is read
 *   only by the AI's knowledge tools.
 *
 * Every call keeps the session on a 403 (`aiChanges/api.js`).
 */
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import * as React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { DataAPIContext } from '../../api';
import PageMeta from '../../components/PageMeta';
import { UntrustedBlock, UntrustedText } from '../../components/UntrustedText';
import * as api from '../../notifications/aiChanges/api';
import { BASE as AI_CHANGES_PATH, fmtTs } from '../../notifications/aiChanges/parts';
import { ContextStore } from '../../store/contextStore';
import VocabularyEditDialog from './VocabularyEditDialog';
import {
    ACTION_WORDS,
    BindingText,
    FindingsAndWarnings,
    originLabel,
    PAGE_PATH,
    routeText,
    VOCABULARY_APPROVE,
    VOCABULARY_WRITE,
    VocabularyRecords,
} from './vocabularyWords';

const GRANTS = ['mcp_write', 'mcp_approve'];

const NoPermission = () => (
    <Alert severity="info" sx={{ mb: 2 }}>
        {`The AI vocabulary needs “${VOCABULARY_WRITE}” or “${VOCABULARY_APPROVE}”, granted explicitly in an access group. `
            + 'Full access does not include them, and only someone who holds one may grant it: ask an administrator who does.'}
    </Alert>
);

const LEGEND = 'Terms, other words and definitions are shown exactly as written: by your team, by Curated Planet support '
    + 'for you, or taken from your set-up. Your AI client reads them as your company\'s data, never as instructions.';

const ORIGIN_COLOR = { tenant: 'primary', ops: 'secondary', auto: 'default' };

const SOURCES = [
    ['', 'All'],
    ['tenant', 'Your company'],
    ['ops', 'Curated Planet support'],
    ['auto', 'From your set-up'],
];

const matches = (entry, query) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [entry.term, entry.definition, entry.concept_name, ...(entry.aliases || [])]
        .some((text) => String(text || '').toLowerCase().includes(q));
};

/** The edits waiting for an approver (`VocabularyPendingChange`): what they change, who asked and why. */
const PendingChanges = ({ pending }) => (
    <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>{`Waiting for approval (${pending.length})`}</Typography>
        {pending.map((p) => (
            <Paper key={p.id} variant="outlined" sx={{ p: 2, mb: 1 }} data-testid={`vocabulary-pending-${p.id}`}>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                    {`${fmtTs(p.created_ts)} · ${p.requested_by || 'Someone'}`}
                </Typography>
                <Box component="ul" sx={{ m: 0, mb: 1, pl: 2.5 }}>
                    {(p.items || []).map((item, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Typography key={i} component="li" variant="body2">
                            <Box component="span" sx={{ fontWeight: 500 }}>{ACTION_WORDS[item.action] || item.action}</Box>
                            {' '}
                            {item.term ? <UntrustedText inline text={item.term} /> : <Box component="span">{`entry #${item.entry}`}</Box>}
                        </Typography>
                    ))}
                </Box>
                <UntrustedText label="Reason" text={p.reason} empty="No reason given." />
                {p.can_approve ? (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        <RouterLink to={AI_CHANGES_PATH}>Review it in AI changes › Approvals</RouterLink>
                    </Typography>
                ) : (
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                        {`Waiting for someone with “${VOCABULARY_APPROVE}” on all tags.`}
                    </Typography>
                )}
            </Paper>
        ))}
    </Box>
);

/** Where the last proposed edit went, and what it changes. */
const ProposeResult = ({ result, name, onClose }) => {
    const route = routeText(result, name);
    return (
        <Alert severity={route.severity} onClose={onClose} sx={{ mb: 2 }} data-testid="vocabulary-result">
            <Typography variant="body2">{route.text}</Typography>
            {route.inbox && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <RouterLink to={AI_CHANGES_PATH}>Open AI changes › Approvals</RouterLink>
                </Typography>
            )}
            {result.applied && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                    It is one change set, listed in AI changes, and can be reverted there.
                </Typography>
            )}
            <VocabularyRecords records={result.records} />
            <FindingsAndWarnings findings={result.findings} warnings={result.warnings} />
        </Alert>
    );
};

const EntriesTable = ({ entries, canPropose, onEdit, onRemove, onDefine }) => (
    <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" aria-label="AI vocabulary entries">
            <TableHead>
                <TableRow>
                    <TableCell>Term</TableCell>
                    <TableCell>Stands for</TableCell>
                    <TableCell>Means</TableCell>
                    <TableCell>Definition</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Last change</TableCell>
                    {canPropose && <TableCell align="right">Actions</TableCell>}
                </TableRow>
            </TableHead>
            <TableBody>
                {entries.map((e) => (
                    <TableRow key={e.id}>
                        <TableCell sx={{ minWidth: 140 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}><UntrustedText inline text={e.term} /></Typography>
                            {e.aliases?.length > 0 && (
                                <Typography variant="caption" color="text.secondary" component="div" sx={{ wordBreak: 'break-word' }}>
                                    {`Also: ${e.aliases.join(', ')}`}
                                </Typography>
                            )}
                        </TableCell>
                        <TableCell>{e.concept_name || e.concept}</TableCell>
                        <TableCell sx={{ minWidth: 160 }}>
                            <BindingText binding={e.binding} names={e.binding_names} />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 360 }}>
                            <Typography variant="body2"><UntrustedText inline text={e.definition} empty="—" /></Typography>
                        </TableCell>
                        <TableCell>
                            <Chip size="small" variant="outlined" color={ORIGIN_COLOR[e.origin] || 'default'} label={originLabel(e.origin)} />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{e.updated_ts ? fmtTs(e.updated_ts) : '—'}</TableCell>
                        {canPropose && (
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                {e.editable ? (
                                    <>
                                        <Button size="small" onClick={() => onEdit(e)} aria-label={`Edit ${e.term}`}>Edit</Button>
                                        <Button size="small" color="error" onClick={() => onRemove(e)} aria-label={`Remove ${e.term}`}>Remove</Button>
                                    </>
                                ) : (
                                    <Button size="small" onClick={() => onDefine(e)} aria-label={`Define your own ${e.term}`}>Define your own</Button>
                                )}
                            </TableCell>
                        )}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </Box>
);

/** Why the viewer may not propose here (the backend's `permissions` and `enabled` on the operator shown). */
const ProposeNotice = ({ data, explicit }) => {
    const name = data.tour_operator.name;
    if (!data.enabled) {
        return (
            <Alert severity="info" sx={{ mb: 2 }}>
                {`Editing the AI vocabulary of ${name} is switched off for now (by Curated Planet): the entries are read-only.`}
            </Alert>
        );
    }
    if (data.permissions?.propose) return null;
    if (explicit('vocabulary', 'mcp_write')) {
        return (
            <Alert severity="info" sx={{ mb: 2 }}>
                {`Proposing an edit needs “${VOCABULARY_WRITE}” on all tags of ${name}: the vocabulary belongs to the whole company.`}
            </Alert>
        );
    }
    return (
        <Alert severity="info" sx={{ mb: 2 }}>
            {`You may approve the edits others propose, in AI changes › Approvals; proposing one needs “${VOCABULARY_WRITE}”.`}
        </Alert>
    );
};

/** `?company=<id>`: the company of the inbox request the page was opened from; null when absent or not an id. */
const requestCompany = (search) => {
    const raw = new URLSearchParams(search).get('company');
    return raw && /^\d+$/.test(raw) ? Number(raw) : null;
};

const Vocabulary = ({ explicit }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { isFranchise } = React.useContext(ContextStore) || {};
    const company = requestCompany(useLocation().search);
    const [state, setState] = React.useState({ loading: true, data: null, error: null });
    const [reload, setReload] = React.useState(0);
    const [query, setQuery] = React.useState('');
    const [source, setSource] = React.useState('');
    const [editing, setEditing] = React.useState(null); // { mode, entry?, prefill? }
    const [result, setResult] = React.useState(null);

    React.useEffect(() => {
        let alive = true;
        api.getVocabulary(dataAPI, { tourOperatorId: company })
            .then((data) => alive && setState({ loading: false, data, error: null }))
            .catch((error) => alive && setState((prev) => ({ loading: false, data: prev.data, error })));
        return () => {
            alive = false;
        };
    }, [dataAPI, reload, company]);

    if (state.loading) return <CircularProgress size={24} sx={{ mb: 2 }} />;
    if (state.error && !state.data) {
        if (state.error.status === 403) return <NoPermission />;
        if (state.error.status === 404) {
            return (
                <Alert severity="info">
                    {isFranchise
                        ? 'Your AI vocabulary permissions do not reach this company: they count on the company they were granted '
                        + 'for. Pick another company in the sidebar.'
                        : 'The AI vocabulary was not found (or is not visible to you).'}
                </Alert>
            );
        }
        return <Alert severity="error">{`Could not load the AI vocabulary: ${state.error.message}`}</Alert>;
    }

    const { data } = state;
    const name = data.tour_operator.name;
    const canPropose = !!(data.enabled && data.permissions?.propose);
    const entries = (data.entries || []).filter((e) => (!source || e.origin === source) && matches(e, query));

    const submit = async (edit) => {
        const answer = await api.proposeVocabularyEdit(dataAPI, data.tour_operator.id, edit);
        setEditing(null);
        setResult(answer);
        setReload((n) => n + 1);
    };

    return (
        <Box>
            {(isFranchise || company !== null) && <Typography variant="subtitle1" sx={{ mb: 1 }}>{`Company: ${name}`}</Typography>}
            {company !== null && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {'This is the company of the request you opened it from, whatever company the sidebar shows. '}
                    <RouterLink to={PAGE_PATH}>Show the company the sidebar shows</RouterLink>
                </Typography>
            )}
            {state.error && <Alert severity="error" sx={{ mb: 2 }}>{`Could not reload the AI vocabulary: ${state.error.message}`}</Alert>}
            <ProposeNotice data={data} explicit={explicit} />
            {result && <ProposeResult result={result} name={name} onClose={() => setResult(null)} />}
            {data.pending?.length > 0 && <PendingChanges pending={data.pending} />}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
                <TextField size="small" label="Search" value={query} onChange={(ev) => setQuery(ev.target.value)} />
                <TextField
                    select
                    size="small"
                    label="Source"
                    value={source}
                    onChange={(ev) => setSource(ev.target.value)}
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 200 }}
                >
                    {SOURCES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </TextField>
                <Box sx={{ flex: 1 }} />
                {canPropose && <Button variant="contained" onClick={() => setEditing({ mode: 'create' })}>New entry</Button>}
            </Stack>
            <UntrustedBlock title="Your company's words" legend={LEGEND}>
                {entries.length ? (
                    <EntriesTable
                        entries={entries}
                        canPropose={canPropose}
                        onEdit={(entry) => setEditing({ mode: 'update', entry })}
                        onRemove={(entry) => setEditing({ mode: 'delete', entry })}
                        onDefine={(entry) => setEditing({ mode: 'create', prefill: entry })}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        {(data.entries || []).length ? 'No entry matches.' : 'No entries yet.'}
                    </Typography>
                )}
            </UntrustedBlock>
            {editing && (
                <VocabularyEditDialog
                    mode={editing.mode}
                    entry={editing.entry}
                    prefill={editing.prefill}
                    data={data}
                    onCancel={() => setEditing(null)}
                    onSubmit={submit}
                />
            )}
        </Box>
    );
};

const AIVocabularyPage = () => {
    const { hasExplicitPerm } = React.useContext(ContextStore) || {};
    const explicit = (scope, action) => typeof hasExplicitPerm === 'function' && hasExplicitPerm(scope, action);
    const holds = GRANTS.some((action) => explicit('vocabulary', action));
    return (
        <Box p={2} sx={{ maxWidth: 1200 }}>
            <PageMeta title="AI vocabulary" />
            <Typography variant="h6">AI vocabulary</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Your company&apos;s own words for the things your team asks its AI client about: “LATAM” means these regions,
                “Tour Leader” this staff role. An AI client connected over MCP reads them to understand your questions.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Nothing guests see changes: notifications, the app, booking pages and tours never read them, and they never
                change who counts as a guide.
            </Typography>
            {holds ? <Vocabulary explicit={explicit} /> : <NoPermission />}
        </Box>
    );
};

export default AIVocabularyPage;
